'use client';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getAllPharmacies,
  togglePharmacyStatus,
  validatePharmacyCsv,
  importPharmacyCsv
} from 'features/admin/adminApi';


import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';
import Badge from 'components/base/Badge';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BulkUploadModal from 'components/modals/BulkUploadModal'

import {
  faSearch,
  faStoreAlt,
  faLocationDot,
  faEnvelope,
  faToggleOn,
  faToggleOff,
  faTimes,
  faCheckCircle,
  faTimesCircle,
  faSpinner,
  faBuildingColumns,
  faEye,
  faUpload,
  faFileCsv
} from '@fortawesome/free-solid-svg-icons';

import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';

interface Pharmacy {
  id: number;
  user_id: number;
  name: string;
  email: string;
  pharmacy_name: string;
  location: string;
  license_number: string;
  is_active: number;
  is_approved: number;
  created_at: string;
}

const ManagePharmacyPage = () => {
  const navigate = useNavigate();

  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');

  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const [search, setSearch] = useState('');

  const [page, setPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);

  const [total, setTotal] = useState(0);

  const [stats, setStats] = useState({
    active: 0,
    inactive: 0,
    approved: 0,
    pending: 0
  });

  const [showUploadModal, setShowUploadModal] = useState(false);

  // FETCH PHARMACIES
  const fetchPharmacies = async (pageNumber = 1) => {
    try {
      setLoading(true);
      setError('');

      const data = await getAllPharmacies({
        page: pageNumber,
        limit: 10,
        search
      });

      setPharmacies(data.pharmacies || []);
      setPage(data.page || 1);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);

      // Calculate stats
      const pharmacyData = data.pharmacies || [];
      setStats({
        active: pharmacyData.filter((p: any) => p.is_active).length,
        inactive: pharmacyData.filter((p: any) => !p.is_active).length,
        approved: pharmacyData.filter((p: any) => p.is_approved).length,
        pending: pharmacyData.filter((p: any) => !p.is_approved).length
      });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch pharmacies');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPharmacies(1);
  }, []);

  // SEARCH
  const handleSearch = () => {
    fetchPharmacies(1);
  };

  // TOGGLE STATUS
  const handleToggleStatus = async (id: number) => {
    try {
      setActionLoading(id);
      await togglePharmacyStatus(id);
      await fetchPharmacies(page);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
      console.error('Toggle error:', err);
    } finally {
      setActionLoading(null);
    }
  };



  return (
    <Container className="py-2 py-lg-4" fluid>
      {/* Header Section */}
     <Row className="mb-4">
        <Col>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <p className="text-body-tertiary text-uppercase fs-10 fw-bold mb-1">
                <FontAwesomeIcon icon={faBuildingColumns} className="me-2" />
                Admin Control Center
              </p>
              <h2 className="text-body-emphasis mb-1">Pharmacy Management</h2>
              <p className="mb-0 text-body-tertiary fs-9">
                Monitor, approve, and manage pharmacy registrations and active status.
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4 g-3">
        <Col md={6} lg={3}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Total Pharmacies
                  </p>
                  <h3 className="text-body-emphasis mb-0">{total}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon icon={faStoreAlt} className="text-primary" />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={6} lg={3}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Active
                  </p>
                  <h3 className="text-success mb-0">{stats.active}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon icon={faToggleOn} className="text-success " />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={6} lg={3}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Approved
                  </p>
                  <h3 className="text-info mb-0">{stats.approved}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-info" />
                </span>
              </div>
            </div>
          </div>
        </Col>

        <Col md={6} lg={3}>
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-1">
                    Pending Review
                  </p>
                  <h3 className="text-warning mb-0">{stats.pending}</h3>
                </div>
                <span className="avatar avatar-m text-center">
                  <FontAwesomeIcon icon={faTimesCircle} className="text-warning" />
                </span>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" className="mb-0" onClose={() => setError('')} dismissible>
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Search Section */}
       <Row className="mb-4">
        <Col lg={7}>
          <SearchBox
            placeholder="Search by pharmacy name, email, or license number..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleSearch()}
          />
        </Col>

         
            
        <Col lg={5}>
          <div className="d-flex gap-2 justify-content-end">

            <Button
              variant="phoenix-secondary"
              startIcon={<FontAwesomeIcon icon={faFileCsv} />}
              onClick={() => setShowUploadModal(true)}
            >
              Upload CSV
            </Button>

            <Button
              variant="phoenix-primary"
              onClick={handleSearch}
              startIcon={<FontAwesomeIcon icon={faSearch} />}
            >
              Search
            </Button>

          </div>
        </Col>
      </Row>

      {/* Loading State */}
      {loading && (
        <Row className="py-5">
          <Col className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-body-tertiary fw-semibold">Loading pharmacy data...</p>
          </Col>
        </Row>
      )}

      {/* Table Section */}
      {!loading && (
        <Row>
          <Col>
            <div className="card border-0 shadow-sm overflow-hidden">
              <div className="table-responsive">
                <table className="table align-middle mb-0 fs-9">
                  <thead className="bg-body-secondary">
                    <tr>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0">
                        Pharmacy
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        License
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Location
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Approval
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Status
                      </th>
                      <th className="text-body-emphasis fw-bold text-uppercase ps-4 pe-4 py-3 border-0 text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {pharmacies.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-6 border-0">
                          <div className="mb-3">
                            <FontAwesomeIcon
                              icon={faStoreAlt}
                              className="text-body-tertiary"
                              size="2x"
                            />
                          </div>
                          <p className="text-body-tertiary fw-semibold mb-0">
                            No pharmacies found
                          </p>
                        </td>
                      </tr>
                    ) : (
                      pharmacies.map(pharmacy => (
                        <tr key={pharmacy.id} className="border-bottom border-translucent hover-row">
                          <td className="ps-4 pe-4 py-3 border-0">
                            <div className="d-flex align-items-center gap-2">
                              <div className="avatar avatar-m flex-shrink-0">
                                <span className="avatar-initials rounded-circle bg-success-subtle">
                                  <FontAwesomeIcon
                                    icon={faStoreAlt}
                                    className="text-success"
                                  />
                                </span>
                              </div>
                              <div className="flex-grow-1 min-width-0">
                                <p className="mb-0 fw-semibold text-body-emphasis text-truncate">
                                  {pharmacy.pharmacy_name || pharmacy.name}
                                </p>
                                <p className="mb-0 text-body-tertiary fs-10 text-truncate">
                                  <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                                  {pharmacy.email}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <span className="badge bg-light text-body-emphasis fw-semibold">
                              {pharmacy.license_number || '-'}
                            </span>
                          </td>

                          <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <span className="text-body-emphasis fw-semibold">
                              <FontAwesomeIcon
                                icon={faLocationDot}
                                className="me-2 text-danger"
                              />
                              {pharmacy.location || 'N/A'}
                            </span>
                          </td>

                          <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <Badge
                              bg={pharmacy.is_approved ? 'success' : 'warning'}
                              className="rounded-pill fw-semibold"
                            >
                              {pharmacy.is_approved ? (
                                <>
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                  Approved
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                                  Pending
                                </>
                              )}
                            </Badge>
                          </td>

                          <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <Badge
                              bg={pharmacy.is_active ? 'success' : 'danger'}
                              className="rounded-pill fw-semibold"
                            >
                              {pharmacy.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>

                          <td className="ps-4 pe-4 py-3 border-0 text-center">
                            <div className="d-flex gap-2 justify-content-center">
                              <Button
                                size="sm"
                                variant="info"
                                onClick={() =>
                                  navigate(`/dashboard/admin/user-management/pharmacy/${pharmacy.id}`)
                                }
                                startIcon={
                                  <FontAwesomeIcon icon={faEye} />
                                }
                                title="View Details"
                              >
                                View
                              </Button>



                              <Button
                                size="sm"
                                variant={
                                  pharmacy.is_active ? 'subtle-danger' : 'subtle-success'
                                }
                                onClick={() => handleToggleStatus(pharmacy.id)}
                                disabled={actionLoading === pharmacy.id}
                                startIcon={
                                  actionLoading === pharmacy.id ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={
                                        pharmacy.is_active ? faToggleOff : faToggleOn
                                      }
                                    />
                                  )
                                }
                                title={pharmacy.is_active ? 'Deactivate' : 'Activate'}
                              >
                                {pharmacy.is_active ? 'Deactivate' : 'Activate'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!loading && pharmacies.length > 0 && (
                <div className="card-footer bg-body border-top border-translucent py-3">
                  <ul className="pagination justify-content-center mb-0 gap-1">
                    {page > 1 && (
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => fetchPharmacies(page - 1)}
                        >
                          Previous
                        </button>
                      </li>
                    )}

                    {[...Array(totalPages)].map((_, i) => (
                      <li
                        key={i}
                        className={`page-item ${page === i + 1 ? 'active' : ''}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => fetchPharmacies(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))}

                    {page < totalPages && (
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => fetchPharmacies(page + 1)}
                        >
                          Next
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </Col>
        </Row>
      )}

      <BulkUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        title="Bulk Upload Existing Pharmacies"
        entityName="Pharmacies"
        sampleType="pharmacy"
        validateApi={validatePharmacyCsv}
        importApi={importPharmacyCsv}
        onSuccess={() => {
          fetchPharmacies(page);
        }}
      />

      <style>{`
        .hover-row:hover {
          background-color: rgba(0, 0, 0, 0.02);
          transition: background-color 0.2s ease;
        }
        .min-width-0 {
          min-width: 0;
        }
      `}</style>
    </Container>
  );
};

export default ManagePharmacyPage;